import { createContext, useCallback, useContext, useMemo } from "react";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { z } from "zod";
import { PROJECT_HISTORY_FEED_FEATURE_FLAG } from "@remi/constants";
import { OrganizationType } from "@remi/enums/organization-type";
import { hasCapability } from "@remi/permissions";
import { REMI_INTERNAL_ORG } from "@remi/types/organizations";

import { useUserProfile } from "~/contexts/UserProfileContext";
import { preservedInboxSearch } from "~/hooks/inbox/preservedInboxQuery";
import { useBidOrganizations } from "../useBid";
import { useContractorProfile } from "../useContractorProfile";
import useNavigation from "../useNavigation";
import { useProjectOrganizations } from "../useProjectOrganizations";

export const FeedTabValues = ["REMI", "HOMEOWNER", "CONTRACTOR", "PARTNER", "ACTIVITY_LOG", "PROJECT_HISTORY"] as const;
const FeedTabSchema = z.enum(FeedTabValues);
export type FeedTab = z.infer<typeof FeedTabSchema>;
export const FeedTab = FeedTabSchema.enum;

export const FeedTabToOrganizationType: Partial<Record<FeedTab, OrganizationType>> = {
  [FeedTab.REMI]: OrganizationType.REMI_INTERNAL,
  [FeedTab.HOMEOWNER]: OrganizationType.HOMEOWNER,
  [FeedTab.CONTRACTOR]: OrganizationType.CONTRACTOR,
  [FeedTab.PARTNER]: OrganizationType.SOLAR_COMPANY,
};

// Keyed by path, not by tab: a missing entry compiles clean and surfaces only as a dead deep link, so
// the round-trip is asserted in a test.
export const PathToFeedTab: Record<string, FeedTab> = {
  activity: FeedTab.ACTIVITY_LOG,
  history: FeedTab.PROJECT_HISTORY,
  contractor: FeedTab.CONTRACTOR,
  homeowner: FeedTab.HOMEOWNER,
  partner: FeedTab.PARTNER,
  remi: FeedTab.REMI,
};

export const FeedTabToPath: Record<FeedTab, string> = {
  [FeedTab.ACTIVITY_LOG]: "activity",
  [FeedTab.PROJECT_HISTORY]: "history",
  [FeedTab.CONTRACTOR]: "contractor",
  [FeedTab.HOMEOWNER]: "homeowner",
  [FeedTab.PARTNER]: "partner",
  [FeedTab.REMI]: "remi",
};

const RemiFeedTabDisplayNames: Record<FeedTab, string> = {
  [FeedTab.REMI]: "Remi",
  [FeedTab.HOMEOWNER]: "Homeowner",
  [FeedTab.CONTRACTOR]: "Contractor",
  [FeedTab.PARTNER]: "Partner",
  [FeedTab.ACTIVITY_LOG]: "Activity log",
  [FeedTab.PROJECT_HISTORY]: "History",
};

const ExternalFeedTabDisplayNames: Partial<Record<FeedTab, string>> = {
  [FeedTab.CONTRACTOR]: "Remi",
  [FeedTab.HOMEOWNER]: "Homeowner",
};

type OnFeedNavigateProps = {
  tab: FeedTab;
  conversationId?: string;
  skipHistory?: boolean;
};

/**
 * Set by a surface that hosts the project feed outside its own route (e.g. a drawer over another page):
 * tab and thread changes go here instead of rewriting the URL path, which would navigate off the host page.
 */
export const FeedNavigationOverrideContext = createContext<
  ((target: { tab: FeedTab; conversationId?: string }) => void) | null
>(null);

export const useFeedNavigation = (bidId?: number, projectId?: number) => {
  const { data: bidOrganizations } = useBidOrganizations(bidId);
  const { data: projectOrganizations = [] } = useProjectOrganizations({ projectId });
  const { user } = useUserProfile();
  const { data: contractorProfile } = useContractorProfile(
    user.person.organization.type === OrganizationType.CONTRACTOR ? user.person.organizationId : undefined,
  );

  const { navigate } = useNavigation();
  const pathname = usePathname();
  const router = useRouter();

  const isProxyEnabled = contractorProfile?.isProxyEnabled;

  const partner = useMemo(() => {
    return bidOrganizations?.find((org) => org.type === OrganizationType.SOLAR_COMPANY)?.organization ?? null;
  }, [bidOrganizations]);

  const navigateOverride = useContext(FeedNavigationOverrideContext);
  const onFeedNavigate = useCallback(
    ({ tab, conversationId, skipHistory }: OnFeedNavigateProps) => {
      if (navigateOverride !== null) {
        navigateOverride({ tab, conversationId });
        return;
      }
      const pathLevels = pathname ? pathname.split("/") : [];
      const paths = [];
      const tabPaths = FeedTabValues.map((t) => FeedTabToPath[t]);
      for (const level of pathLevels) {
        if (tabPaths.includes(level)) {
          break;
        }
        paths.push(level);
      }
      paths.push(FeedTabToPath[tab]);
      if (conversationId) {
        paths.push(conversationId);
      }
      void navigate({ pathname: `${paths.join("/")}`, search: preservedInboxSearch(router.query), skipHistory });
    },
    [navigateOverride, pathname, navigate, router.query],
  );

  const getFeedTabDisplayName = useCallback(
    (tab: FeedTab) => {
      if (user.person.organization.id !== REMI_INTERNAL_ORG.id) {
        const display = ExternalFeedTabDisplayNames[tab];
        if (display) {
          return display;
        }
        return "Chat";
      }
      if (tab === FeedTab.PARTNER && partner) {
        return partner.name;
      }
      return RemiFeedTabDisplayNames[tab];
    },
    [user.person.organization.id, partner],
  );

  // ESTIMATOR has view.read.estimating directly; REMI staff inherit it via
  // REMI_ADMIN → ESTIMATOR. navigation.read.insuranceAll is REMI_USER-direct
  // and not held by ESTIMATOR, so it negatives the inherited estimating view.
  // `=== true` because the hook returns undefined until PostHog resolves: an unresolved flag must not
  // offer the tab, or the feed flashes in and out on every cold load.
  const isHistoryFeedEnabled = useFeatureFlagEnabled(PROJECT_HISTORY_FEED_FEATURE_FLAG) === true;

  const isEstimatorOnly =
    hasCapability(user, "view.read.estimating") && !hasCapability(user, "navigation.read.insuranceAll");

  const tabs = useMemo(() => {
    if (user.person.organization.id === REMI_INTERNAL_ORG.id) {
      if (isEstimatorOnly) {
        return [FeedTab.REMI];
      }
      return [
        FeedTab.REMI,
        FeedTab.HOMEOWNER,
        FeedTab.CONTRACTOR,
        partner ? FeedTab.PARTNER : null,
        FeedTab.ACTIVITY_LOG,
        isHistoryFeedEnabled ? FeedTab.PROJECT_HISTORY : null,
      ].filter((t) => t !== null);
    }
    if (user.person.organization.type === OrganizationType.CONTRACTOR) {
      if (isProxyEnabled) {
        return [FeedTab.CONTRACTOR, FeedTab.HOMEOWNER];
      }
      return [FeedTab.CONTRACTOR];
    }
    if (user.person.organization.type === OrganizationType.SOLAR_COMPANY) {
      return [FeedTab.PARTNER];
    }
    return [];
  }, [
    user.person.organization.id,
    user.person.organization.type,
    isEstimatorOnly,
    partner,
    isProxyEnabled,
    isHistoryFeedEnabled,
  ]);

  const allowedTabs = useMemo(() => {
    if (user.person.organization.id === REMI_INTERNAL_ORG.id) {
      if (isEstimatorOnly) {
        return [FeedTab.REMI];
      }
      return [
        FeedTab.REMI,
        FeedTab.HOMEOWNER,
        FeedTab.CONTRACTOR,
        FeedTab.PARTNER,
        FeedTab.ACTIVITY_LOG,
        ...(isHistoryFeedEnabled ? [FeedTab.PROJECT_HISTORY] : []),
      ];
    }
    if (user.person.organization.type === OrganizationType.CONTRACTOR) {
      if (isProxyEnabled) {
        return [FeedTab.CONTRACTOR, FeedTab.HOMEOWNER];
      }
      return [FeedTab.CONTRACTOR];
    }
    if (user.person.organization.type === OrganizationType.SOLAR_COMPANY) {
      return [FeedTab.PARTNER];
    }
    return [];
  }, [
    user.person.organization.id,
    user.person.organization.type,
    isEstimatorOnly,
    isProxyEnabled,
    isHistoryFeedEnabled,
  ]);

  const defaultTab = useMemo(() => {
    if (user.person.organization.type === OrganizationType.CONTRACTOR) {
      return FeedTab.CONTRACTOR;
    }
    if (user.person.organization.type === OrganizationType.SOLAR_COMPANY) {
      return FeedTab.PARTNER;
    }
    if (projectOrganizations.some((o) => o.type === OrganizationType.CONTRACTOR)) {
      return FeedTab.CONTRACTOR;
    }
    return FeedTab.REMI;
  }, [user.person.organization.type, projectOrganizations]);

  const hasTabPermission = useCallback(
    (tab: FeedTab) => {
      return allowedTabs.includes(tab);
    },
    [allowedTabs],
  );

  return { onFeedNavigate, getFeedTabDisplayName, tabs, defaultTab, hasTabPermission };
};
